<template>
  <div
    v-click-outside="(event: MouseEvent) => emit('click-outside', event)"
    class="chat-panel"
    :class="{ 'chat-panel--wide': agentMode }"
  >
    <transition name="simple-fade-in-out">
      <div v-if="isOpen" class="chat-panel__inner">
        <div class="chat-panel__collapse" @click="emit('close-icon-click')" v-html="collapseIcon" />

        <header class="chat-panel__header">
          <div class="chat-panel__title-row">
            <h2 class="chat-panel__title">Chat &amp; Tasks</h2>
            <div class="chat-panel__header-actions">
              <button
                v-if="!agentMode"
                type="button"
                class="chat-panel__icon-btn"
                :class="{ 'chat-panel__icon-btn--active': searchOpen }"
                title="Search and filter"
                @click="toggleSearch"
              >
                <span class="chat-panel__icon chat-panel__icon--search" v-html="searchIcon" />
              </button>
            </div>
          </div>

          <!-- Stays put while a person filter is on, so the chips remain the one
               place that says what the feed is narrowed to. -->
          <chat-search-field
            v-if="!agentMode && (searchOpen || userFilter.length)"
            ref="searchFieldRef"
            v-model="userFilter"
            v-model:query="query"
            class="chat-panel__search"
          />

          <div class="chat-panel__tabs-row">
            <chat-filters v-model:type-filter="typeFilter" :counts="tabCounts" :agent="agentEnabled" />
          </div>
        </header>

        <agent-panel v-if="agentMode" class="chat-panel__agent" />

        <!-- Wraps the feed so the jump button can anchor to the bottom of the
             messages. The feed itself scrolls, so anchoring inside it would send
             the button up the page with the content. -->
        <div v-if="!agentMode" class="chat-panel__feed-wrap">
          <div ref="feedRef" class="chat-panel__feed" @scroll="onFeedScroll">
            <template v-if="rows.length">
              <template v-for="row in rows" :key="row.key">
                <div v-if="row.type === 'day'" class="chat-panel__day">
                  <span class="chat-panel__day-chip">{{ row.label }}</span>
                </div>

                <button
                  v-else-if="row.type === 'status'"
                  type="button"
                  class="chat-panel__section"
                  :aria-expanded="!row.collapsed"
                  @click="toggleGroup(row.statusKey)"
                >
                  <span
                    class="chat-panel__section-caret"
                    :class="{ 'chat-panel__section-caret--collapsed': row.collapsed }"
                    v-html="caretIcon"
                  />
                  <span class="chat-panel__status-dot" :class="`chat-panel__status-dot--${row.statusKey}`" />
                  <span class="chat-panel__status-label">{{ row.label }}</span>
                  <span class="chat-panel__status-count">{{ row.count }}</span>
                </button>

                <div v-else-if="row.type === 'unread'" class="chat-panel__unread">
                  <span class="chat-panel__unread-label">New</span>
                  <span class="chat-panel__unread-rule" />
                </div>

                <chat-message-item
                  v-else
                  :message="row.message"
                  :continuation="row.continuation"
                  :mentions-me="row.mentionsMe"
                  @tag-user="onTagUser"
                />
              </template>
            </template>

            <div v-else class="chat-panel__placeholder">
              <span class="chat-panel__placeholder-icon" v-html="chatIcon" />
              {{ placeholderText }}
            </div>
          </div>

          <transition name="chat-jump">
            <!-- Not on the task tabs: those are ranked by urgency, not time, so
                 there is no "latest" at the bottom to jump to. -->
            <button v-if="scrolledUp && !grouped" type="button" class="chat-panel__jump" @click="scrollToLatest">
              Jump to latest
              <span class="chat-panel__jump-icon" v-html="arrowDownIcon" />
            </button>
          </transition>
        </div>

        <chat-composer
          v-if="!agentMode"
          ref="composerRef"
          class="chat-panel__composer"
          @recipients-change="onRecipientsChange"
          @sent="onSent"
        />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
// @ts-expect-error - click-outside-vue3 has no type declarations
import clickOutside from 'click-outside-vue3';
import { isToday, isYesterday, format } from 'date-fns';
import { ChatItemKind, ChatTaskStatus, type ChatMessageDto } from '@/ApiClient';
import { parseServerUtcDate } from '@lib/formatters';
import { compareChatMessages, timeOf } from '@lib/chat-sort';
import { bodyMentionsUser, splitSearchMention } from '@lib/chat-mentions';
import { useChat, type ChatTypeFilter } from '@/store/chat';
import { chatPanel } from '@lib/chat-panel';
import { useAgentChat } from '@/store/agent-chat';
import { useIntercomOutOfTheWay } from '@lib/intercom';
import { useRoles } from '@/store/roles';
import { useBrandUsers } from '@/store/brand-users';
import ChatFilters from './chat-filters.vue';
import ChatSearchField from './chat-search-field.vue';
import ChatMessageItem from './chat-message-item.vue';
import ChatComposer from './chat-composer.vue';
import AgentPanel from '@components/agent-chat/agent-panel.vue';
import chatIcon from '@icons/filters-icons/comment-outlined.svg';
import collapseIcon from '@icons/other/arrow-right.svg';
import searchIcon from '@icons/other/ico-search.svg';
import arrowDownIcon from '@icons/other/arrow-down.svg';
import caretIcon from '@icons/other/caret-down.svg';

const vClickOutside = clickOutside.directive;

const { isOpen } = defineProps<{ isOpen: boolean }>();
const emit = defineEmits<{ 'click-outside': [event: MouseEvent]; 'close-icon-click': [] }>();

const typeFilter = chatPanel.tab;
const userFilter = ref<string[]>([]);
const searchOpen = ref(false);
const query = ref('');
const searchFieldRef = ref<InstanceType<typeof ChatSearchField>>();
const composerRef = ref<InstanceType<typeof ChatComposer>>();
const feedRef = ref<HTMLElement>();

// The agent tab is its own pane: threads with an agent rather than a feed of messages, and
// wide enough to read a transcript. It exists only where the server runs the agent chat.
const agentMode = computed(() => typeFilter.value === 'agent');
const agentEnabled = computed(() => useAgentChat().enabled === true);

// The Intercom bubble sits over the composer's send button while the panel is out.
useIntercomOutOfTheWay(() => isOpen);
useAgentChat().loadStatus();

// The task filters group by status instead of running chronologically.
const grouped = computed(() => typeFilter.value === 'my-tasks' || typeFilter.value === 'others-tasks');

/**
 * Which status groups are folded away. Done starts folded: it is the one group
 * with no natural ceiling — everything finished stays there — so on a busy board
 * it is most of the list. Per-session rather than persisted, since folding is
 * usually "not while I am looking at this", not a standing preference.
 */
const collapsedGroups = ref<string[]>(['done']);

const toggleGroup = (key: string) => {
  collapsedGroups.value = collapsedGroups.value.includes(key)
    ? collapsedGroups.value.filter((existing) => existing !== key)
    : [...collapsedGroups.value, key];
};

// Deliberately not the author's name: people are a facet now, and matching them
// here too would answer "messages from Felicia" differently from the chip —
// substring against the body, and blind to her being a recipient.
const matchesQuery = (message: ChatMessageDto): boolean => {
  // Only the part before any "@": that token is picking a person, and searching
  // bodies for it would match the literal "@Simon" text inside other messages.
  const text = splitSearchMention(query.value).search.toLowerCase();
  if (!text) return true;
  return [message.body, message.contextLabel].some((value) => (value ?? '').toLowerCase().includes(text));
};

// What each tab is scoped to. Shared by the feed and the tab badges so a count
// and the list it labels can never disagree about what belongs in the tab.
const matchesTypeFilter = (message: ChatMessageDto, filter: ChatTypeFilter): boolean => {
  const meId = useChat().meId;
  if (filter === 'agent') return false;
  if (filter === 'messages') return message.kind === ChatItemKind.Message;
  if (filter === 'my-tasks')
    return message.kind === ChatItemKind.Task && message.recipients.some((r) => r.user.id === meId);
  if (filter === 'others-tasks')
    return (
      message.kind === ChatItemKind.Task &&
      message.author?.id === meId &&
      message.recipients.some((r) => r.user.id !== meId)
    );
  return true;
};

// Everything the search and the person chips allow, before a tab narrows it
// further. The badges count from here, so they say how much each tab would show
// under the filters in force rather than across the whole feed.
const scopedMessages = computed<ChatMessageDto[]>(() => {
  const chat = useChat();
  let list = chat.loaded;

  if (userFilter.value.length) {
    // Filtering by your own id matches only self-assigned / note-to-self items —
    // every item involves you, so the usual match would be a no-op. That holds
    // for an id a group put here too: otherwise filtering by a group you belong
    // to pulls in your whole inbox and drowns the group's own traffic.
    const matchesUser = (m: ChatMessageDto, userId: string): boolean => {
      if (userId === chat.meId) {
        return m.author?.id === chat.meId && m.recipients.every((r) => r.user.id === chat.meId);
      }
      return m.author?.id === userId || m.recipients.some((r) => r.user.id === userId);
    };
    list = list.filter((m) => userFilter.value.some((id) => matchesUser(m, id)));
  }

  if (splitSearchMention(query.value).search) list = list.filter(matchesQuery);

  return list;
});

// Sorted live — status changes re-order the list.
const filteredMessages = computed<ChatMessageDto[]>(() =>
  scopedMessages.value.filter((m) => matchesTypeFilter(m, typeFilter.value)).sort(compareChatMessages(grouped.value))
);

// Badge counts everything still outstanding in a tab's scope — open *and*
// ongoing. Only Done stops counting.
const isUnfinishedTask = (message: ChatMessageDto): boolean =>
  message.kind === ChatItemKind.Task && message.status !== ChatTaskStatus.Done;

const tabCounts = computed<Record<ChatTypeFilter, number>>(() => {
  const outstanding = scopedMessages.value.filter(isUnfinishedTask);
  return {
    all: 0,
    messages: 0,
    'my-tasks': outstanding.filter((m) => matchesTypeFilter(m, 'my-tasks')).length,
    'others-tasks': outstanding.filter((m) => matchesTypeFilter(m, 'others-tasks')).length,
    agent: 0,
  };
});

const placeholderText = computed(() => {
  if (useChat().loading) return 'Loading…';
  const filtering = userFilter.value.length || splitSearchMention(query.value).search;
  return filtering ? 'No matches' : 'Nothing here yet';
});

const toggleSearch = () => {
  searchOpen.value = !searchOpen.value;
  if (searchOpen.value) nextTick(() => searchFieldRef.value?.focus());
  else query.value = '';
};

// ── Unread divider ─────────────────────────────────────────────────────────
// Where the divider sits, kept until the user changes tab. Held separately from
// the seen flags because opening the panel clears those — see the open watch.
const unreadFromId = ref<number | null>(null);

const firstUnreadId = (): number | null => {
  const chat = useChat();
  const unread = chat.loaded.filter(
    (m) => m.author?.id !== chat.meId && m.recipients.some((r) => r.user.id === chat.meId && !r.seen)
  );
  if (!unread.length) return null;
  return unread.reduce((oldest, m) => (timeOf(m) < timeOf(oldest) ? m : oldest)).id;
};

// Marking seen belongs to the panel rather than to whatever opened it: showing
// the messages is what makes them seen. It also has to happen here so it runs
// after the capture above — markAllSeen wipes the very flags firstUnreadId reads,
// and ordering two watches in two components was never guaranteed.
watch(
  () => isOpen,
  (open) => {
    if (!open) return;
    const chat = useChat();

    const capture = () => {
      unreadFromId.value = firstUnreadId();
      chat.markAllSeen();
      nextTick(() => scrollToLatest());
    };

    if (!chat.loading) return capture();

    // Opened mid-load: the messages that decide where the divider goes have not
    // arrived, so firstUnreadId would read an empty list and place nothing.
    // One-shot, and hung off the open rather than off `loading` itself — a
    // SignalR update reloads the feed while the panel sits open, and recapturing
    // then would move a divider that is meant to hold until you change tab.
    const stop = watch(
      () => chat.loading,
      (loading) => {
        if (loading) return;
        stop();
        // Closed again while we waited — nothing to place, and nothing seen.
        if (isOpen) capture();
      }
    );
  },
  { immediate: true }
);

watch(typeFilter, () => {
  unreadFromId.value = null;
  nextTick(() => scrollToLatest());
});

// ── Feed rows ──────────────────────────────────────────────────────────────
type FeedRow =
  | { key: string; type: 'day'; label: string }
  | { key: string; type: 'status'; statusKey: string; label: string; count: number; collapsed: boolean }
  | { key: string; type: 'unread' }
  | { key: string; type: 'item'; message: ChatMessageDto; continuation: boolean; mentionsMe: boolean };

const dayLabel = (date: Date): string => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'd MMMM yyyy');
};

const statusGroups = [
  { status: ChatTaskStatus.Open, key: 'open', label: 'Open' },
  { status: ChatTaskStatus.Ongoing, key: 'ongoing', label: 'Ongoing' },
  { status: ChatTaskStatus.Done, key: 'done', label: 'Done' },
];

// Hoisted: mentionsMe() runs once per row on every recompute of a list that
// re-sorts live, and rebuilding this array per message was most of that cost.
const mentionableRoles = computed(() => useRoles().loaded.map((role) => ({ id: role.id, name: role.name })));

const mentionsMe = (message: ChatMessageDto): boolean => {
  const meId = useChat().meId;
  if (!meId) return false;
  return bodyMentionsUser(message.body, useBrandUsers().loaded, mentionableRoles.value, meId);
};

const rows = computed<FeedRow[]>(() => {
  const list = filteredMessages.value;
  const result: FeedRow[] = [];

  if (grouped.value) {
    for (const group of statusGroups) {
      const items = list.filter((m) => m.status === group.status);
      if (!items.length) continue;

      // The header stays whatever happens: folded away, it is the only thing
      // left saying the group exists and how much is in it.
      const collapsed = collapsedGroups.value.includes(group.key);
      result.push({
        key: `status-${group.key}`,
        type: 'status',
        statusKey: group.key,
        label: group.label,
        count: items.length,
        collapsed,
      });
      if (collapsed) continue;

      for (const message of items) {
        result.push({
          key: `item-${message.id}`,
          type: 'item',
          message,
          continuation: false,
          mentionsMe: mentionsMe(message),
        });
      }
    }
    return result;
  }

  let lastDay = '';
  let lastRunKey: string | undefined;
  let unreadPlaced = false;

  // Same author AND same set of people — a note to self must not disappear into
  // a run addressed to someone else, since the header line is the only place
  // that difference is shown.
  const runKeyOf = (message: ChatMessageDto): string =>
    [message.author?.id ?? 'system', ...message.recipients.map((r) => r.user.id).sort()].join('|');

  for (const message of list) {
    const created = parseServerUtcDate(message.createdAt);
    const day = format(created, 'yyyy-MM-dd');
    const isTask = message.kind === ChatItemKind.Task;
    const mentioned = mentionsMe(message);

    if (day !== lastDay) {
      result.push({ key: `day-${day}`, type: 'day', label: dayLabel(created) });
      lastDay = day;
      lastRunKey = undefined;
    }

    if (!unreadPlaced && unreadFromId.value === message.id) {
      result.push({ key: 'unread', type: 'unread' });
      unreadPlaced = true;
      lastRunKey = undefined;
    }

    // A run is broken by a different author or audience, a task, a day divider,
    // or a message that mentions you — each deserves its own header line.
    const runKey = runKeyOf(message);
    const continuation = !isTask && !mentioned && lastRunKey != null && runKey === lastRunKey;

    result.push({ key: `item-${message.id}`, type: 'item', message, continuation, mentionsMe: mentioned });
    lastRunKey = isTask || mentioned ? undefined : runKey;
  }

  return result;
});

// ── Scrolling ──────────────────────────────────────────────────────────────
const scrolledUp = ref(false);
const JUMP_THRESHOLD = 220;

const onFeedScroll = () => {
  const el = feedRef.value;
  if (!el) return;
  scrolledUp.value = el.scrollHeight - el.clientHeight - el.scrollTop > JUMP_THRESHOLD;
};

const scrollToLatest = () => {
  const el = feedRef.value;
  if (!el || grouped.value) return;
  el.scrollTop = el.scrollHeight;
  scrolledUp.value = false;
  // Rows can still grow after this tick (avatars, an autosizing body), which
  // would leave the newest item half under the composer — settle on the next frame.
  requestAnimationFrame(() => {
    if (feedRef.value) feedRef.value.scrollTop = feedRef.value.scrollHeight;
  });
};

// New items land at the bottom — follow them unless the user has scrolled away.
watch(
  () => filteredMessages.value.length,
  () => {
    if (!scrolledUp.value) nextTick(() => scrollToLatest());
  }
);

// Tagging users in the composer adds them to the user filter — without
// dropping anyone already filtered for.
const onRecipientsChange = (ids: string[]) => {
  if (ids.length) userFilter.value = [...new Set([...userFilter.value, ...ids])];
};

// A name/avatar clicked in a message tags that user in the composer (no filter change).
const onTagUser = (userId: string) => composerRef.value?.tagUser(userId);

// After sending, jump to the filter matching what was just sent.
const onSent = (payload: { kind: ChatItemKind; recipientIds: string[] }) => {
  // The filters below move the feed to what was just sent; a search term left
  // in the box would filter it straight back out.
  query.value = '';

  if (payload.kind === ChatItemKind.Message) {
    typeFilter.value = 'messages';
    userFilter.value = [...payload.recipientIds];
  } else if (payload.recipientIds.length === 0) {
    // An untagged task is self-assigned.
    typeFilter.value = 'my-tasks';
    userFilter.value = [];
  } else {
    typeFilter.value = 'others-tasks';
    userFilter.value = [...payload.recipientIds];
  }
  nextTick(() => scrollToLatest());
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';
@import '@styles/media-queries.scss';

.chat-panel {
  width: 50rem;
  height: $page-content-min-height;
  box-sizing: border-box;
  background: $c-surface;
  // Directional, the way the article flyout's is. An all-round shadow casts
  // upward too, and at the top edge that draws a soft seam across the join with
  // the header — the panel reads as a card floating under the bar rather than as
  // a surface that slid out of it.
  box-shadow: -1.2rem 0 3.2rem rgba($c-ink, 0.18);
  transition: width 0.2s ease;

  // A transcript with tool calls and tables needs more than a message feed does.
  &--wide {
    width: 104rem;
    max-width: calc(100vw - 6rem);
  }

  // Full screen on a phone. There is no page left to see beside it at this
  // width, and the two panes fail in opposite directions without this: the feed
  // is wider than the viewport and hangs off the left edge, while the agent
  // keeps a gutter it cannot afford for a transcript.
  @include mobile-only {
    &,
    &--wide {
      width: 100vw;
      max-width: 100vw;
    }

    // The collapse control straddles the panel's left edge, which is the screen
    // edge now — so it comes inside, where a thumb can reach it, and the header
    // starts clear of where it lands.
    .chat-panel__collapse {
      inset: 2.4rem auto auto 1.2rem;
    }

    .chat-panel__header {
      padding-left: 4.4rem;
    }
  }

  &__agent {
    flex: 1;
    min-height: 0;
  }

  &__inner {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    position: relative;
  }

  &__collapse {
    width: 2.4rem;
    height: 2.4rem;
    box-sizing: border-box;
    border: 2px solid rgba($c-ink, 0.5);
    border-radius: $br-pill;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 2.4rem auto auto -1.2rem;
    z-index: 3;
    background: $c-surface;
    transition: border-color $transition-fast ease;
    cursor: pointer;
    user-select: none;

    &:hover {
      border-color: $c-ink;
    }

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      position: relative;
      right: -1px;
      fill: rgba($c-ink, 0.5);
      transition: fill $transition-fast ease;
    }

    &:hover :deep(svg) {
      fill: $c-ink;
    }
  }

  // ── Header ───────────────────────────────────────────────────────────────
  // Same top edge as the article slider's frozen header: an opaque canvas band
  // the feed's white passes under, with a hairline marking the join.
  &__header {
    flex: none;
    padding: 1.9rem 2.4rem 0;
    background: $c-canvas;
    border-bottom: 1px solid $c-hairline;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__title {
    // The same heading as the notifications panel's, which sits beside this one from the same
    // row of icons — the shorthand is what resets the h2's bold to the weight that one uses.
    margin: 0;
    font:
      2rem/2rem 'Montserrat',
      sans-serif;
    color: $c-ink;
  }

  &__header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  &__icon-btn {
    width: 3rem;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: $br-medium-heavy;
    background: none;
    cursor: pointer;
    transition: background $transition-fast ease;

    &:hover,
    &--active {
      background: $c-surface-muted;
    }
  }

  &__icon {
    display: flex;

    :deep(svg) {
      fill: $c-ink;
    }

    &--search :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      opacity: 0.5;
    }
  }

  &__search {
    margin-top: 1.2rem;
  }

  &__tabs-row {
    display: flex;
    align-items: center;
    gap: 1.6rem;
    margin-top: 1.6rem;
  }

  &__tabs-row :deep(.chat-filters) {
    flex: 1;
    min-width: 0;
  }

  // ── Feed ─────────────────────────────────────────────────────────────────
  &__feed-wrap {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__feed {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: 1rem;
  }

  // A centred chip marking where a day starts. It rides with the conversation
  // rather than pinning to the top, so scrolling carries it away instead of
  // leaving it parked over the messages.
  &__day {
    display: flex;
    justify-content: center;
    padding: 1.4rem 2.4rem 0.6rem;
    pointer-events: none;
  }

  &__day-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1.2rem;
    border-radius: $br-pill;
    background: $c-surface;
    box-shadow: $shadow-pill;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba($c-ink, 0.5);
  }

  // Status groups stay a left-aligned section header — they label a block, they
  // don't mark a point in time.
  &__section {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    padding: 2.2rem 2.4rem 1rem;
    border: none;
    background: $c-surface;
    cursor: pointer;
    text-align: left;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba($c-ink, 0.5);
  }

  &__section-caret {
    display: flex;
    flex: none;
    transition: transform $transition-fast ease;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: rgba($c-ink, 0.5);
    }

    &--collapsed {
      transform: rotate(-90deg);
    }
  }

  &__status-dot {
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    flex: none;

    &--open {
      background: $c-orange;
    }
    &--ongoing {
      background: $c-blue;
    }
    &--done {
      background: $c-green;
    }
  }

  &__status-label {
    color: $c-ink;
  }

  &__status-count {
    font-family: 'Lato', sans-serif;
    font-size: 1.2rem;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
    color: rgba($c-ink, 0.5);
  }

  &__unread {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.4rem 2.4rem 0.6rem;
  }

  &__unread-label {
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $c-on-accent-soft;
  }

  &__unread-rule {
    flex: 1;
    height: 1px;
    background: $c-accent;
  }

  &__placeholder {
    height: 100%;
    min-height: 24rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    font-size: 1.4rem;
    color: rgba($c-ink, 0.45);
  }

  &__placeholder-icon {
    width: 3.4rem;
    height: 3.4rem;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: $c-ink;
      opacity: 0.25;
    }
  }

  // ── Jump to latest ───────────────────────────────────────────────────────
  &__jump {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    // Rides the bottom of the messages. Anchoring it to the panel instead meant
    // guessing the composer's height, which changes as the textarea grows.
    bottom: 1.6rem;
    z-index: 4;
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    height: 3.4rem;
    padding: 0 1.6rem;
    border: none;
    border-radius: $br-pill;
    background: $c-ink;
    color: $c-surface;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow:
      0 2px 4px rgba($c-shadow, 0.12),
      0 12px 28px -8px rgba($c-shadow, 0.34);
  }

  &__jump-icon {
    display: flex;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: $c-surface;
    }
  }

  &__composer {
    flex: none;
  }
}

.chat-jump-enter-active,
.chat-jump-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.chat-jump-enter-from,
.chat-jump-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px) scale(0.97);
}

@media (max-width: $desktop-width) {
  .chat-panel {
    height: calc(100vh - 5.6rem);
  }
}

@media (max-width: $tablet-p-width) {
  .chat-panel {
    height: calc(100vh - 4.9rem);
  }
}
</style>
